import { Controller } from "@hotwired/stimulus"

// Right-click context menu for company names in the person signals list.
// Allows quick blacklisting without navigating to settings.
//
// Usage:
//   <div data-controller="context-menu" data-context-menu-company-value="Acme Corp">
//     <span>Acme Corp</span>
//   </div>
export default class extends Controller {
  static values = { company: String }

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleEscape = this.handleEscape.bind(this)
    this.handleContextMenu = this.handleContextMenu.bind(this)

    this.element.addEventListener("contextmenu", this.handleContextMenu)
    document.addEventListener("click", this.handleOutsideClick)
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    this.element.removeEventListener("contextmenu", this.handleContextMenu)
    document.removeEventListener("click", this.handleOutsideClick)
    document.removeEventListener("keydown", this.handleEscape)
    this.removeMenu()
  }

  handleContextMenu(event) {
    if (!this.companyValue) return

    event.preventDefault()
    event.stopPropagation()

    this.removeMenu()
    this.showMenu(event.clientX, event.clientY)
  }

  showMenu(x, y) {
    const menu = document.createElement("div")
    menu.id = "context-menu-popup"
    menu.className = "fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px]"
    menu.style.left = `${x}px`
    menu.style.top = `${y}px`

    const button = document.createElement("button")
    button.type = "button"
    button.className = "w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
    button.innerHTML = `
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
      </svg>
      Blacklist <strong>${this.escapeHtml(this.companyValue)}</strong>
    `
    button.addEventListener("click", () => this.blacklist())

    menu.appendChild(button)

    // Ensure menu stays within viewport
    document.body.appendChild(menu)
    const rect = menu.getBoundingClientRect()
    if (rect.right > window.innerWidth) {
      menu.style.left = `${x - rect.width}px`
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${y - rect.height}px`
    }

    this.menu = menu
  }

  async blacklist() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    try {
      const response = await fetch("/settings/blacklist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ value: this.companyValue, entry_type: "company_name" })
      })

      const data = await response.json()

      if (data.success) {
        // Find the parent row and fade it out
        const row = this.element.closest("[data-signal-row]")
        if (row) {
          row.style.transition = "opacity 0.3s ease-out"
          row.style.opacity = "0"
          setTimeout(() => row.remove(), 300)
        }
      } else {
        alert(data.message || "Failed to blacklist company.")
      }
    } catch (error) {
      alert("Failed to blacklist company. Please try again.")
    }

    this.removeMenu()
  }

  handleOutsideClick(event) {
    if (this.menu && !this.menu.contains(event.target)) {
      this.removeMenu()
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.removeMenu()
    }
  }

  removeMenu() {
    if (this.menu) {
      this.menu.remove()
      this.menu = null
    }
    // Also clean up any orphaned menus
    document.getElementById("context-menu-popup")?.remove()
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
